import React, { useEffect, useState } from 'react';
import orderBy from 'lodash/orderBy';
import range from 'lodash/range';
import ReactMarkdown from 'react-markdown';

import Button from '@material-ui/core/Button';
import Dialog, { DialogProps } from '@material-ui/core/Dialog';
import ImageList from '@material-ui/core/ImageList';
import ImageListItem from '@material-ui/core/ImageListItem';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import SvgIcon from '@material-ui/core/SvgIcon';
import Paper from '../Paper';

import * as LoyaltyRewardService from '../../api/loyaltyRewardsService';
import * as RewardService from '../../api/rewardService';

import { ReactComponent as StampIcon } from '../../assets/icons/Stamp.svg';
import { ReactComponent as CheckIcon } from '../../assets/icons/Check.svg';
import { ReactComponent as CrownIcon } from '../../assets/icons/Crown.svg';

const useStampStyles = makeStyles((theme) => ({
  root: ({ highlight }: { highlight: boolean }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: highlight ? 2 : 1,
    borderStyle: 'solid',
    borderColor: highlight
      ? theme.palette.primary.main
      : theme.palette.grey[300],
    width: 40,
    height: 40,
    borderRadius: 40 / 2,
  }),
  icon: {
    color: theme.palette.primary.main,
  },
  fillRoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderStyle: 'solid',
    borderColor: theme.palette.primary.main,
    width: 40,
    height: 40,
    borderRadius: 40 / 2,
    backgroundColor: theme.palette.primary.main,
  },
}));

export function Stamp({
  value,
  highlight,
  currentStamp,
}: {
  value: number;
  highlight: boolean;
  currentStamp: number;
}) {
  const classes = useStampStyles({ highlight });

  return (
    <>
      {value <= currentStamp ? (
        <div className={classes.fillRoot}>
          <SvgIcon
            component={CheckIcon}
            color="primary"
            className={classes.icon}
            viewBox={'0 0 30 30'}
          />
        </div>
      ) : (
        <div className={classes.root}>
          {highlight ? (
            <SvgIcon
              component={StampIcon}
              color="primary"
              className={classes.icon}
            />
          ) : (
            <Typography variant="subtitle2">{value}</Typography>
          )}
        </div>
      )}
    </>
  );
}

const useMilestoneStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 16,
  },
  image: {
    width: 36,
    height: 36,
    marginRight: 10,
    borderRadius: 2,
    objectFit: 'cover',
  },
  content: {
    minWidth: 0, // Ensure the child text overflow ellipsis works on a flex child
  },
  stamps: {
    fontWeight: theme.typography.fontWeightBold,
  },
  title: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
}));

/**
 * Milestone renders the milestone list item
 * @param milestone
 */
export function MilestoneListItem({
  milestone,
}: {
  milestone: LoyaltyRewardService.RedemptionMilestone;
}) {
  const classes = useMilestoneStyles();
  const [reward, setReward] = useState<RewardService.Reward | null>(null);

  useEffect(() => {
    const fetchMilestoneReward = async () => {
      const res = await RewardService.get(milestone.reward.id);
      setReward(res);
    };

    fetchMilestoneReward();
  }, [milestone]);

  if (!reward) {
    return null;
  }

  return (
    <div className={classes.root}>
      {reward && reward.images && reward.images[0].versions.xl && (
        <div>
          <img
            src={reward.images[0].versions.xl}
            alt={reward.title}
            className={classes.image}
          />
        </div>
      )}
      <div style={{ minWidth: 0 }}>
        <Typography variant="subtitle2" className={classes.stamps}>
          {milestone.threshold} Stamps get {reward.title}
        </Typography>
      </div>
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  grow: {
    flexGrow: 1,
  },
  actionContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  toolbar: {
    height: 70,
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
    padding: theme.spacing(3),
  },
  box: {
    flex: 1,
    display: 'flex',
  },
  close: {
    marginRight: 'auto',
  },
  actions: {
    marginLeft: 'auto',
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
  },
  subTitle: {
    fontWeight: theme.typography.fontWeightBold,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    paddingLeft: theme.spacing(4),
  },
  closeIcon: {
    color: theme.palette.common.black,
  },
  selectedMerchantText: {
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.primary.main,
    textAlign: 'left',
  },
  unselectedMerchantText: {
    fontWeight: theme.typography.fontWeightBold,
    textAlign: 'left',
  },
  homeContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: theme.spacing(),
    marginTop: theme.spacing(4),
  },
  contentContainer: {
    color: theme.palette.grey[500],
  },
  avatar: {
    height: 80,
    width: 80,
    boxShadow: '0 2px 10px rgba(199, 199, 199, 1)',
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(4),
  },

  listItemContainer: {
    marginLeft: 0,
    transition: 'none !important',
    backgroundColor: 'inherit !important',
  },
  progress: {
    fontWeight: theme.typography.fontWeightBold,
    textAlign: 'left',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    color: '#ea710a',
  },
  progressContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingLeft: theme.spacing(4),
  },
  crownIcon: {
    marginRight: theme.spacing(2),
  },
  tile: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(),
  },
  stampCardRoot: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing(4),
  },
  cardContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(),
    width: 350,
    padding: theme.spacing(3),
  },
  descCardContainer: {
    width: 350,
    padding: theme.spacing(3),
  },
  descContainer: {
    maxWidth: 350,
    marginBottom: theme.spacing(2),
  },
  rewardCardContainer: {
    width: 350,
    padding: theme.spacing(3),
  },
  rewardContainer: {
    maxWidth: 350,
    marginBottom: theme.spacing(2),
  },
  rewardTitle: {
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.common.black,
    marginBottom: theme.spacing(2),
  },
  markdown: {
    fontFamily: 'ClarikaGeometric',
    fontSize: 16,
  },
  descTitle: {
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.common.black,
  },
  milestonesContainer: {
    marginTop: '5px',
  },
  actionMilestonesContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  actionMilestonesButton: {
    marginTop: 2,
    marginBottom: theme.spacing(),
  },
}));

type Props = Pick<DialogProps, 'open' | 'onClose'> & {
  milestoneList: LoyaltyRewardService.RedemptionMilestone[];
  stampCardName: string;
  quantity: number;
  maxStamps: number;
  description?: string;
};

export default function ViewCardDetailsDialog({
  onClose,
  open,
  milestoneList,
  stampCardName,
  quantity,
  maxStamps,
  description,
}: Props) {
  const classes = useStyles();
  const [showMoreRewards, setShowMoreRewards] = useState<boolean>(false);
  const highlightedKeys = milestoneList.map((m) => m.threshold);

  const handleClose = () => {
    if (onClose) {
      onClose({}, 'backdropClick');
    }
  };

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <div className={classes.toolbar}>
        <div className={classes.homeContainer}>
          <Typography variant="h4" className={classes.title}>
            {stampCardName}
          </Typography>
        </div>

        <IconButton onClick={handleClose}>
          <CloseIcon className={classes.closeIcon} />
        </IconButton>
      </div>
      <div className={classes.contentContainer}>
        <div className={classes.progressContainer}>
          <SvgIcon
            component={CrownIcon}
            color="primary"
            className={classes.crownIcon}
          />
          <Typography variant="h5" className={classes.progress} gutterBottom>
            {quantity < maxStamps
              ? `Your Progress: ${quantity} of ${maxStamps}`
              : 'Completed!'}
          </Typography>
        </div>
        <div className={classes.stampCardRoot}>
          <Paper className={classes.cardContainer}>
            <ImageList
              rowHeight={40}
              cols={maxStamps < 5 ? maxStamps : 5}
              gap={16}
              className={classes.tile}
            >
              {range(1, maxStamps + 1).map((x) => {
                return (
                  <ImageListItem
                    key={x}
                    cols={1}
                    rows={1}
                    className={classes.tile}
                  >
                    <Stamp
                      key={x}
                      value={x}
                      highlight={highlightedKeys.includes(x) && x > quantity}
                      currentStamp={quantity}
                    />
                  </ImageListItem>
                );
              })}
            </ImageList>
          </Paper>
        </div>
        {description && description.length > 0 && (
          <div className={classes.stampCardRoot}>
            <div className={classes.descContainer}>
              <Paper className={classes.descCardContainer}>
                <Typography
                  variant="h5"
                  className={classes.descTitle}
                  gutterBottom
                >
                  Description
                </Typography>
                <ReactMarkdown className={classes.markdown}>
                  {description}
                </ReactMarkdown>
              </Paper>
            </div>
          </div>
        )}
        <div className={classes.stampCardRoot}>
          {milestoneList.length > 0 && (
            <div className={classes.rewardContainer}>
              <Paper className={classes.rewardCardContainer}>
                <Typography
                  variant="h5"
                  className={classes.rewardTitle}
                  gutterBottom
                >
                  Rewards to Earn
                </Typography>
                {milestoneList.length > 0 && (
                  <div className={classes.milestonesContainer}>
                    {!showMoreRewards && milestoneList.length > 5
                      ? orderBy(milestoneList, ['threshold'])
                          .slice(0, 5)
                          .map((m, idx) => (
                            <MilestoneListItem key={idx} milestone={m} />
                          ))
                      : orderBy(milestoneList, ['threshold']).map((m, idx) => (
                          <MilestoneListItem key={idx} milestone={m} />
                        ))}
                  </div>
                )}
                {milestoneList.length > 5 && !showMoreRewards && (
                  <div className={classes.actionMilestonesContainer}>
                    <Button
                      className={classes.actionMilestonesButton}
                      onClick={() => setShowMoreRewards(true)}
                      color="default"
                    >
                      {'View More Rewards'}
                    </Button>
                  </div>
                )}
              </Paper>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
}
